import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { CheckCircle2, Mail, Rocket, ShieldCheck, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { Editable } from "@/components/builder/Editable";
import { useContent } from "@/context/ContentContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const BENEFITS = [
  { icon: Rocket, text: "Provalo subito, gratis e senza impegno" },
  { icon: Mail, text: "Rispondiamo alle tue domande via email" },
  { icon: ShieldCheck, text: "Nessuna chiamata: decidi tu quando e come" },
];

export const LeadForm = () => {
  const { ctaClick } = useContent();
  const [form, setForm] = useState({ email: "", messaggio: "", consenso: false });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const update = (key, value) => {
    setForm((f) => ({ ...f, [key]: value }));
    setErrors((e) => ({ ...e, [key]: undefined }));
  };

  const validate = () => {
    const e = {};
    if (!form.email.trim()) e.email = "Inserisci la tua email.";
    else if (!emailRegex.test(form.email.trim())) e.email = "Inserisci un'email valida.";
    if (!form.messaggio.trim() || form.messaggio.trim().length < 5) e.messaggio = "Scrivi la tua domanda (min. 5 caratteri).";
    if (!form.consenso) e.consenso = "È necessario accettare il trattamento dei dati.";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    setLoading(true);
    try {
      await axios.post(`${API}/leads`, {
        email: form.email.trim(),
        messaggio: form.messaggio.trim(),
        consenso: form.consenso,
        source: "domanda",
      });
      setSubmitted(true);
      toast.success("Domanda inviata!", { description: "Ti risponderemo via email al più presto." });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error("Invio non riuscito", { description: typeof detail === "string" ? detail : "Controlla i dati inseriti e riprova tra poco." });
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contatti" className="section-lavender">
      <div className="mx-auto grid max-w-6xl gap-10 px-4 py-16 sm:px-6 lg:grid-cols-2 lg:px-8 lg:py-24">
        <div className="lg:pr-6">
          <p className="mb-3 text-xs font-bold uppercase tracking-[0.16em] text-primary"><Editable path="contact.eyebrow">Hai domande?</Editable></p>
          <h2 className="font-display text-4xl font-extrabold text-foreground sm:text-5xl"><Editable path="contact.title">Scrivici, ti rispondiamo.</Editable></h2>
          <p className="mt-4 text-base leading-7 text-muted-foreground">
            <Editable path="contact.desc">Hai una domanda su Sedio? Scrivici qui e ti rispondiamo via email. Se invece vuoi vederlo subito all'opera, accedi gratis e provalo: nessuna chiamata, nessun impegno.</Editable>
          </p>
          <ul className="mt-8 space-y-4">
            {BENEFITS.map((b, i) => (
              <li key={i} className="flex items-center gap-3">
                <span className="inline-flex h-9 w-9 flex-none items-center justify-center rounded-xl bg-white text-primary shadow-sm">
                  <b.icon className="h-5 w-5" strokeWidth={2} />
                </span>
                <span className="text-sm text-foreground md:text-base">{b.text}</span>
              </li>
            ))}
          </ul>
          <div className="mt-8">
            <Button data-testid="contact-trial-button" size="lg" onClick={ctaClick("contact.cta.link", "https://app.sedio.it")} className="btn-cta h-12 gap-2 px-7 text-base">
              <Editable path="contact.cta">Provalo gratis</Editable> <ArrowRight className="h-4 w-4" />
            </Button>
          </div>
        </div>

        <div className="card-soft p-6 sm:p-8">
          {submitted ? (
            <motion.div initial={{ opacity: 0, scale: 0.98 }} animate={{ opacity: 1, scale: 1 }} className="flex h-full flex-col items-center justify-center py-10 text-center" data-testid="contact-form-success">
              <span className="inline-flex h-14 w-14 items-center justify-center rounded-full bg-primary/10 text-primary">
                <CheckCircle2 className="h-7 w-7" />
              </span>
              <h3 className="mt-5 font-display text-2xl font-extrabold text-foreground">Domanda inviata!</h3>
              <p className="mt-2 max-w-sm text-sm leading-6 text-muted-foreground">
                Grazie! Ti risponderemo via email all'indirizzo <strong>{form.email}</strong>.
              </p>
              <Button variant="outline" className="mt-6 rounded-full" data-testid="contact-form-reset-button" onClick={() => { setSubmitted(false); setForm({ email: "", messaggio: "", consenso: false }); }}>
                Invia un'altra domanda
              </Button>
            </motion.div>
          ) : (
            <form onSubmit={handleSubmit} noValidate data-testid="contact-form">
              <div className="grid gap-4">
                <div>
                  <Label htmlFor="email">Email *</Label>
                  <Input id="email" type="email" data-testid="contact-form-email-input" value={form.email} onChange={(e) => update("email", e.target.value)} placeholder="nome@email.it" className="mt-1.5 rounded-xl" aria-invalid={!!errors.email} />
                  {errors.email && (<p data-testid="contact-form-email-error" className="mt-1 text-xs text-destructive">{errors.email}</p>)}
                </div>
                <div>
                  <Label htmlFor="messaggio">La tua domanda *</Label>
                  <Textarea id="messaggio" data-testid="contact-form-message-textarea" value={form.messaggio} onChange={(e) => update("messaggio", e.target.value)} placeholder="Scrivici cosa vuoi sapere su Sedio" className="mt-1.5 min-h-[120px] rounded-xl" aria-invalid={!!errors.messaggio} />
                  {errors.messaggio && (<p data-testid="contact-form-message-error" className="mt-1 text-xs text-destructive">{errors.messaggio}</p>)}
                </div>
                <div>
                  <div className="flex items-start gap-3">
                    <Checkbox id="consenso" data-testid="contact-form-consent-checkbox" checked={form.consenso} onCheckedChange={(v) => update("consenso", v === true)} className="mt-0.5" />
                    <Label htmlFor="consenso" className="text-sm font-normal leading-6 text-muted-foreground">Acconsento al trattamento dei miei dati per ricevere una risposta. *</Label>
                  </div>
                  {errors.consenso && (<p data-testid="contact-form-consent-error" className="mt-1 text-xs text-destructive">{errors.consenso}</p>)}
                </div>
              </div>
              <Button type="submit" data-testid="contact-form-submit-button" disabled={loading} className="btn-cta mt-6 h-12 w-full text-base">
                {loading ? "Invio in corso..." : "Invia domanda"}
              </Button>
              <p className="mt-3 text-center text-xs text-muted-foreground">Ti rispondiamo via email. Consulta la nostra <a href="/privacy" className="underline hover:text-foreground">Privacy Policy</a> e i <a href="/termini" className="underline hover:text-foreground">Termini</a>.</p>
            </form>
          )}
        </div>
      </div>
    </section>
  );
};
